import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import {
  FlaskConical,
  AlertCircle,
  Database,
  Monitor,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

/**
 * TestSeedTab — Reseeds a canonical 8-load test set (4 SVE + 4 Balaji,
 * across 4 lifecycle states). Only visible in lower-env deployments.
 *
 * The parent (Admin.js) already gates rendering on `is_test_env` returned
 * by GET /api/admin/env-info, but this tab also self-hides if that flag
 * flips false at runtime (defensive).
 */
const RUNS_STORAGE_KEY = 'admin.testSeed.runsByKey.v1';
const SEEDING_KEY_STORAGE = 'admin.testSeed.uiSeedingKey.v1';

const loadRunsFromStorage = () => {
  try {
    const raw = localStorage.getItem(RUNS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export default function TestSeedTab() {
  const [available, setAvailable] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [seeding, setSeeding] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  // uiSeedingKey — which button is currently in-flight ('generated/sve/single/super').
  // Persisted so navigating away and back keeps buttons disabled while a run is live.
  const [uiSeedingKey, setUiSeedingKey] = useState(
    () => localStorage.getItem(SEEDING_KEY_STORAGE) || null
  );
  // Per-button last-run state, keyed by `${mode}/${client}/${invoiceType}/${role}`
  // (single-variant) or `${mode}/ALL/${role}` (Run-All).
  // Values: { status: 'running' | 'success' | 'error', jobId?, result?, error?,
  //           expanded?, progress? }.
  // Persisted so the progress bar + last-run badges survive a tab switch.
  const [runsByKey, setRunsByKey] = useState(() => loadRunsFromStorage());

  // Persist runsByKey to localStorage on every change so tab-switches don't
  // wipe the progress bar / result badges. Also mirror uiSeedingKey.
  useEffect(() => {
    try {
      localStorage.setItem(RUNS_STORAGE_KEY, JSON.stringify(runsByKey));
    } catch {
      /* quota exceeded / disabled — nothing we can do */
    }
  }, [runsByKey]);

  useEffect(() => {
    if (uiSeedingKey) localStorage.setItem(SEEDING_KEY_STORAGE, uiSeedingKey);
    else localStorage.removeItem(SEEDING_KEY_STORAGE);
  }, [uiSeedingKey]);

  // On mount, resume polling for any run that was still 'running' when the
  // user last navigated away. We look up the persisted runsByKey (already
  // rehydrated from localStorage) and, for each entry with status 'running'
  // and a jobId, kick off the poll loop again. This keeps the progress bar
  // and last-run badges accurate across tab switches.
  useEffect(() => {
    const stored = loadRunsFromStorage();
    Object.entries(stored).forEach(([key, run]) => {
      if (run?.status !== 'running' || !run.jobId) return;
      const isRunAll = key.split('/')[1] === 'ALL';
      (async () => {
        try {
          const final = await pollUntilDone(run.jobId, key, isRunAll);
          const runOk = final.status === 'success';
          setRunsByKey((prev) => ({
            ...prev,
            [key]: {
              status: runOk ? 'success' : 'error',
              result: final.result || { error: final.error, ...(final.params || {}) },
              expanded: false,
            },
          }));
          if (runOk) toast.success(`Resumed run ${key} finished successfully`);
          else if (final.error) toast.error(`Resumed run ${key}: ${final.error}`);
          else toast.error(`Resumed run ${key} finished with errors`);
        } catch (err) {
          const msg = err.response?.data?.detail || err.message;
          setRunsByKey((prev) => ({
            ...prev,
            [key]: {
              status: 'error',
              result: { success: false, error: msg },
              expanded: false,
            },
          }));
        } finally {
          setUiSeedingKey((cur) => (cur === key ? null : cur));
        }
      })();
    });
    // Only run once on mount — pollUntilDone is intentionally not a dep since
    // we snapshot the persisted state at mount time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await axios.get(`${API}/admin/env-info`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAvailable(Boolean(r.data?.is_test_env));
      } catch {
        setAvailable(false);
      }
    })();
  }, []);

  const handleSeed = async () => {
    if (confirmText !== 'RESEED TEST LOADS') {
      toast.error('Please type "RESEED TEST LOADS" exactly to confirm');
      return;
    }
    try {
      setSeeding(true);
      const token = localStorage.getItem('token');
      const r = await axios.post(
        `${API}/admin/seed-test-loads`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLastResult(r.data);
      toast.success(`Seeded ${r.data.seeded_count} test loads (counter → ${r.data.counter})`);
      setShowDialog(false);
      setConfirmText('');
    } catch (error) {
      toast.error(
        'Seed failed: ' + (error.response?.data?.detail || error.message)
      );
    } finally {
      setSeeding(false);
    }
  };

  // Shared polling loop — polls the status endpoint every N seconds until
  // the job leaves 'running', writing live progress into runsByKey. Called
  // from both fresh-click handlers below and the mount-time resume effect.
  const pollUntilDone = async (jobId, key, isRunAll) => {
    const token = localStorage.getItem('token');
    const authHeaders = { Authorization: `Bearer ${token}` };
    const pollInterval = isRunAll ? 5000 : 3000;
    /* eslint-disable no-await-in-loop */
    while (true) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      try {
        const statusRes = await axios.get(
          `${API}/admin/seed-ui-load/status`,
          { headers: authHeaders, params: { job_id: jobId }, timeout: 30000 }
        );
        const s = statusRes.data;
        if (s.status !== 'running') return s;
        setRunsByKey((prev) => {
          const cur = prev[key] || {};
          const nextProgress = isRunAll
            ? {
                done: s.variants_done || 0,
                total: s.variants_total || 4,
                current: s.current_variant || null,
                variantCasesDone: s.progress?.cases_done || 0,
                variantCasesTotal: s.progress?.cases_total || null,
              }
            : {
                done: s.progress?.cases_done || 0,
                total: s.progress?.cases_total || null,
                last: s.progress?.last_case || null,
              };
          return {
            ...prev,
            [key]: { ...cur, status: 'running', jobId, progress: nextProgress },
          };
        });
      } catch (pollErr) {
        const st = pollErr.response?.status;
        // 404 means the backend forgot the job (pod restart) — bail cleanly.
        if (st === 404) {
          return { status: 'error', error: 'Backend restarted — job state was lost. Please rerun.', result: null };
        }
        if (st && st >= 400 && st < 500) throw pollErr;
        // 5xx / network blips → keep polling silently.
      }
    }
    /* eslint-enable no-await-in-loop */
  };

  const handleUiSeed = async (role = 'super', clientKey = 'sve', invoiceType = 'single', invoiceMode = 'generated') => {
    const key = `${invoiceMode}/${clientKey}/${invoiceType}/${role}`;
    const token = localStorage.getItem('token');
    const authHeaders = { Authorization: `Bearer ${token}` };
    try {
      setUiSeedingKey(key);
      // POST kicks off the background job and returns quickly with a job_id.
      // Cloudflare's edge idle timeout is never in play (POST is ~200 ms).
      const startRes = await axios.post(
        `${API}/admin/seed-ui-load`,
        {},
        {
          headers: authHeaders,
          params: { role, client: clientKey, invoice_type: invoiceType, invoice_mode: invoiceMode },
          timeout: 30000,
        }
      );
      const jobId = startRes.data.job_id;
      if (!jobId) throw new Error('Seed endpoint did not return a job_id');
      // Mark running + jobId in one shot so a tab-switch during the run can
      // always resume by reading the persisted jobId back from localStorage.
      setRunsByKey((prev) => ({
        ...prev,
        [key]: { status: 'running', jobId, expanded: false, startedAt: Date.now() },
      }));

      const final = await pollUntilDone(jobId, key, false);
      const runOk = final.status === 'success';
      const result = final.result || null;
      setRunsByKey((prev) => ({
        ...prev,
        [key]: {
          status: runOk ? 'success' : 'error',
          result: result || { error: final.error, ...(final.params || {}) },
          expanded: false,
        },
      }));
      if (runOk) {
        const cases = (result?.results || []).length;
        const dur = result?.duration_seconds;
        toast.success(
          `UI seed (${role}/${clientKey}/${invoiceType}/${invoiceMode}) done — ${cases} load${cases === 1 ? '' : 's'}${typeof dur === 'number' ? ` in ${dur.toFixed(1)}s` : ''}`
        );
      } else {
        toast.error('UI seed finished with errors — see the panel under the button');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error('UI seed failed: ' + msg);
      setRunsByKey((prev) => ({
        ...prev,
        [key]: {
          status: 'error',
          result: {
            success: false,
            role,
            client: clientKey,
            invoice_type: invoiceType,
            invoice_mode: invoiceMode,
            error: msg,
          },
          expanded: false,
        },
      }));
    } finally {
      setUiSeedingKey(null);
    }
  };

  const toggleRunExpanded = (key) => {
    setRunsByKey((prev) => {
      const cur = prev[key];
      if (!cur) return prev;
      return { ...prev, [key]: { ...cur, expanded: !cur.expanded } };
    });
  };

  const handleUiSeedAll = async (role = 'super', invoiceMode = 'generated') => {
    const key = `${invoiceMode}/ALL/${role}`;
    const token = localStorage.getItem('token');
    const authHeaders = { Authorization: `Bearer ${token}` };
    try {
      setUiSeedingKey(key);
      const startRes = await axios.post(
        `${API}/admin/seed-ui-load-all`,
        {},
        {
          headers: authHeaders,
          params: { role, invoice_mode: invoiceMode },
          timeout: 30000,
        }
      );
      const jobId = startRes.data.job_id;
      if (!jobId) throw new Error('Seed-all endpoint did not return a job_id');
      setRunsByKey((prev) => ({
        ...prev,
        [key]: {
          status: 'running', jobId, expanded: false, startedAt: Date.now(),
          progress: { done: 0, total: 4, current: null },
        },
      }));

      const final = await pollUntilDone(jobId, key, true);
      const runOk = final.status === 'success';
      const result = final.result || null;
      setRunsByKey((prev) => ({
        ...prev,
        [key]: {
          status: runOk ? 'success' : 'error',
          result,
          expanded: false,
        },
      }));
      if (runOk) {
        const cases = (result?.results || []).length;
        const dur = result?.duration_seconds;
        toast.success(
          `Run-All (${role}/${invoiceMode}) done — ${cases} case${cases === 1 ? '' : 's'} across 4 variants${typeof dur === 'number' ? ` in ${(dur / 60).toFixed(1)} min` : ''}`
        );
      } else {
        toast.error('Run-All finished with errors — expand the panel to see which variant failed');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error('Run-All failed: ' + msg);
      setRunsByKey((prev) => ({
        ...prev,
        [key]: {
          status: 'error',
          result: { success: false, role, invoice_mode: invoiceMode, error: msg },
          expanded: false,
        },
      }));
    } finally {
      setUiSeedingKey(null);
    }
  };

  if (!available) {
    return (
      <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm" data-testid="test-seed-disabled">
        <div className="flex items-start gap-3 p-4 bg-slate-50 border border-slate-200 rounded-xl">
          <AlertCircle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-slate-600">
            Test-seed is disabled in this environment. It is only available in
            lower-env deployments (ALLOW_TEST_SEED=true).
          </p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-4 sm:p-6 rounded-2xl border-0 shadow-sm">
        <div className="flex flex-col gap-6">
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 text-sm mb-1">
                Lower-Environment Only
              </h3>
              <p className="text-sm text-amber-800">
                This action wipes <strong>ALL</strong> silica loads, invoice
                counters, assigned sequences, SVE summary invoices and auditor
                records — then reseeds a fixed 8-load test set. Do not run on
                production data.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <FlaskConical className="w-8 h-8 text-emerald-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 mb-1">
                Reseed 8 Canonical Test Loads
              </h3>
              <p className="text-sm text-slate-600 mb-3">
                Creates 4 loads per client (SVE, Balaji), one for each
                lifecycle state: <strong>PAID</strong>, <strong>DELIVERY PENDING</strong>,
                <strong> BILL PENDING</strong>, <strong>PAST DUE</strong>. Each run yields
                the same 8 loads (idempotent).
              </p>
              <Button
                variant="destructive"
                onClick={() => setShowDialog(true)}
                data-testid="reseed-test-loads-btn"
              >
                <Database className="w-4 h-4 mr-2" />
                Wipe & Reseed Test Loads
              </Button>
            </div>
          </div>

          {/* UI-driven seed — two mode sections, each a 4-variant × 2-role grid */}
          <div className="border-t border-slate-200 pt-6 flex items-start gap-3">
            <Monitor className="w-8 h-8 text-indigo-600 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 mb-1">
                Create Loads via Real UI — pick client, invoice type, mode, and role
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Launches Playwright to drive Chromium through the actual{' '}
                <strong>AddEditLoadDialog</strong>. Each button runs only the
                cases matching the chosen client + invoice type. Loads don't
                wipe existing data — every run appends with unique timestamped
                vehicle plates.
              </p>
              {[
                {
                  mode: 'generated',
                  title: 'Generated Invoice (Gen INV + Stamp & Sign)',
                  hint: 'Invoices auto-generated by the system with the auto-signed PDF. * manual-override gap-fill cases run only for Super + Generated (the Manual Invoice No. field is super-only).',
                },
                {
                  mode: 'manual',
                  title: 'Manual Invoice Upload',
                  hint: 'Invoices uploaded as PDFs, no Gen INV. Reproduces the real workflow where a real invoice PDF is uploaded — loads end up with invoice_number=None (the "no invoice" scenario), useful for regression against that bug.',
                },
              ].map(({ mode, title, hint }) => {
                const allKeySuper = `${mode}/ALL/super`;
                const allKeyApprover = `${mode}/ALL/approver`;
                const allRunSuper = runsByKey[allKeySuper];
                const allRunApprover = runsByKey[allKeyApprover];
                const allRunning =
                  allRunSuper?.status === 'running' ||
                  allRunApprover?.status === 'running';
                return (
                <div key={mode} className="mb-5 last:mb-0">
                  <h4 className="font-semibold text-slate-800 text-sm mb-1">{title}</h4>
                  <p className="text-xs text-slate-500 mb-2">{hint}</p>

                  {/* Run-All controls — one row above the per-variant grid. */}
                  <div className="mb-3 p-3 rounded-lg border border-indigo-200 bg-indigo-50">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                      <div className="text-sm font-semibold text-indigo-900 sm:min-w-[280px]">
                        Run ALL 25 cases sequentially
                        <span className="ml-2 text-xs font-normal text-indigo-700">
                          (~25-40 min per run)
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {['super', 'approver'].map((role) => {
                          const k = `${mode}/ALL/${role}`;
                          const running = uiSeedingKey === k;
                          return (
                            <Button
                              key={role}
                              size="sm"
                              onClick={() => handleUiSeedAll(role, mode)}
                              disabled={uiSeedingKey !== null}
                              data-testid={`run-ui-seed-all-${mode}-${role}-btn`}
                              className="min-w-[160px] bg-indigo-600 hover:bg-indigo-700 text-white"
                            >
                              <Monitor className="w-3.5 h-3.5 mr-1.5" />
                              {running
                                ? `Running…`
                                : `Run ALL as ${role === 'super' ? 'Super' : 'Approver'}`}
                            </Button>
                          );
                        })}
                      </div>
                    </div>

                    {allRunning && (
                      <>
                        <div
                          className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-indigo-200"
                          data-testid={`ui-seed-all-progress-${mode}`}
                        >
                          <div className="h-full w-1/3 rounded-full bg-indigo-600 animate-[indeterminate-progress_1.2s_ease-in-out_infinite]" />
                        </div>
                        {['super', 'approver'].map((role) => {
                          const run = runsByKey[`${mode}/ALL/${role}`];
                          if (run?.status !== 'running' || !run.progress) return null;
                          const { done, total, current, variantCasesDone, variantCasesTotal } = run.progress;
                          const inner =
                            variantCasesTotal
                              ? ` (${variantCasesDone} of ${variantCasesTotal} in current)`
                              : '';
                          return (
                            <p
                              key={role}
                              className="mt-1 text-xs text-indigo-800"
                              data-testid={`ui-seed-all-progress-text-${mode}-${role}`}
                            >
                              Variant {done + 1} / {total}
                              {current ? ` — ${current}` : ''}{inner} ({role})
                            </p>
                          );
                        })}
                      </>
                    )}

                    {['super', 'approver'].map((role) => {
                      const k = `${mode}/ALL/${role}`;
                      const run = runsByKey[k];
                      if (!run || run.status === 'running') return null;
                      const res = run.result || {};
                      const ok = run.status === 'success';
                      const totalCases = (res.results || []).length;
                      return (
                        <div
                          key={`${k}-result`}
                          className={`mt-2 rounded-lg border text-xs ${ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
                          data-testid={`ui-seed-all-last-run-${mode}-${role}`}
                        >
                          <button
                            type="button"
                            onClick={() => toggleRunExpanded(k)}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-left"
                          >
                            {run.expanded ? (
                              <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                            )}
                            {ok ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                              <XCircle className="w-3.5 h-3.5 text-red-600" />
                            )}
                            <span className={`font-medium ${ok ? 'text-emerald-800' : 'text-red-800'}`}>
                              Run-All ({role === 'super' ? 'Super' : 'Approver'}):{' '}
                              {ok
                                ? `${totalCases} case${totalCases === 1 ? '' : 's'} across 4 variants`
                                : (res.error || 'failed')}
                            </span>
                            {typeof res.duration_seconds === 'number' && (
                              <span className="text-slate-500">
                                • {(res.duration_seconds / 60).toFixed(1)} min
                              </span>
                            )}
                          </button>
                          {run.expanded && (
                            <div className="px-3 pb-2 space-y-1 text-slate-700">
                              {(res.variants || []).map((v, i) => (
                                <div key={i} className="flex items-center gap-2">
                                  {v.success ? (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                  ) : (
                                    <XCircle className="w-3.5 h-3.5 text-red-600" />
                                  )}
                                  <span className="font-medium">{v.variant}:</span>{' '}
                                  {v.results.length} case
                                  {v.results.length === 1 ? '' : 's'}{' '}
                                  <span className="text-slate-500">
                                    • {v.duration_seconds.toFixed(1)}s
                                  </span>
                                </div>
                              ))}
                              {res.error && <div>Backend error: {res.error}</div>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="space-y-2">
                    {[
                      { client: 'sve',    invoiceType: 'single', label: 'SVE — Single Invoice (8 cases, incl. past-due + summary + manual-override*)' },
                      { client: 'sve',    invoiceType: 'dual',   label: 'SVE — Dual Invoice (8 cases, incl. split-date + past-due)' },
                      { client: 'balaji', invoiceType: 'single', label: 'Balaji — Single Invoice (5 cases, incl. past-due + manual-override*)' },
                      { client: 'balaji', invoiceType: 'dual',   label: 'Balaji — Dual Invoice (4 cases, incl. past-due)' },
                    ].map(({ client, invoiceType, label }) => {
                      const superKey = `${mode}/${client}/${invoiceType}/super`;
                      const approverKey = `${mode}/${client}/${invoiceType}/approver`;
                      const superRun = runsByKey[superKey];
                      const approverRun = runsByKey[approverKey];
                      const anyRunning =
                        superRun?.status === 'running' || approverRun?.status === 'running';
                      return (
                        <div
                          key={`${mode}-${client}-${invoiceType}`}
                          className="py-2 border-b border-slate-100 last:border-b-0"
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                            <div className="text-sm font-medium text-slate-700 sm:min-w-[280px]">
                              {label}
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {['super', 'approver'].map((role) => {
                                const key = `${mode}/${client}/${invoiceType}/${role}`;
                                const running = uiSeedingKey === key;
                                return (
                                  <Button
                                    key={role}
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleUiSeed(role, client, invoiceType, mode)}
                                    disabled={uiSeedingKey !== null}
                                    data-testid={`run-ui-seed-${mode}-${role}-${client}-${invoiceType}-btn`}
                                    className="min-w-[130px]"
                                  >
                                    <Monitor className="w-3.5 h-3.5 mr-1.5" />
                                    {running
                                      ? `Running…`
                                      : `Run as ${role === 'super' ? 'Super' : 'Approver'}`}
                                  </Button>
                                );
                              })}
                            </div>
                          </div>

                          {anyRunning && (
                            <>
                              <div
                                className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-indigo-100"
                                data-testid={`ui-seed-progress-${mode}-${client}-${invoiceType}`}
                              >
                                <div className="h-full w-1/3 rounded-full bg-indigo-500 animate-[indeterminate-progress_1.2s_ease-in-out_infinite]" />
                              </div>
                              {['super', 'approver'].map((role) => {
                                const run = runsByKey[`${mode}/${client}/${invoiceType}/${role}`];
                                if (run?.status !== 'running' || !run.progress) return null;
                                const { done, total, last } = run.progress;
                                return (
                                  <p
                                    key={role}
                                    className="mt-1 text-xs text-indigo-700"
                                    data-testid={`ui-seed-progress-text-${mode}-${role}-${client}-${invoiceType}`}
                                  >
                                    Case {done}{total ? ` of ${total}` : ''} done
                                    {last ? ` — last: ${last}` : ''} ({role === 'super' ? 'Super' : 'Approver'})
                                  </p>
                                );
                              })}
                            </>
                          )}

                          {['super', 'approver'].map((role) => {
                            const key = `${mode}/${client}/${invoiceType}/${role}`;
                            const run = runsByKey[key];
                            if (!run || run.status === 'running') return null;
                            const res = run.result || {};
                            const ok = run.status === 'success';
                            return (
                              <div
                                key={`${key}-result`}
                                className={`mt-2 rounded-lg border text-xs ${ok ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}
                                data-testid={`ui-seed-last-run-${mode}-${role}-${client}-${invoiceType}`}
                              >
                                <button
                                  type="button"
                                  onClick={() => toggleRunExpanded(key)}
                                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left"
                                  data-testid={`ui-seed-last-run-toggle-${mode}-${role}-${client}-${invoiceType}`}
                                >
                                  {run.expanded ? (
                                    <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5 text-slate-500" />
                                  )}
                                  {ok ? (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                  ) : (
                                    <XCircle className="w-3.5 h-3.5 text-red-600" />
                                  )}
                                  <span className={`font-medium ${ok ? 'text-emerald-800' : 'text-red-800'}`}>
                                    Last run ({role === 'super' ? 'Super' : 'Approver'}):{' '}
                                    {ok
                                      ? `${(res.results || []).length} load${(res.results || []).length === 1 ? '' : 's'} ok`
                                      : (res.error || 'failed')}
                                  </span>
                                  {typeof res.duration_seconds === 'number' && (
                                    <span className="text-slate-500">• {res.duration_seconds.toFixed(1)}s</span>
                                  )}
                                </button>
                                {run.expanded && (
                                  <ul className="px-3 pb-2 space-y-0.5 text-slate-700">
                                    {(res.results || []).map((r, i) => (
                                      <li key={i}>
                                        {r.client
                                          ? `${i + 1}. ${r.client} → ${r.flow || 'basic'} → ${r.status}`
                                          : `${i + 1}. Error: ${r.error} — ${r.message}`}
                                      </li>
                                    ))}
                                    {res.error && !res.results && <li>Backend error: {res.error}</li>}
                                  </ul>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {lastResult && (
            <div className="border border-slate-200 rounded-xl p-4 bg-slate-50" data-testid="seed-result-panel">
              <p className="text-sm font-semibold text-slate-800 mb-2">
                Last run — seeded {lastResult.seeded_count} loads (FY {lastResult.fy_key}, counter → {lastResult.counter})
              </p>
              <div className="overflow-x-auto">
                <table className="text-xs w-full">
                  <thead>
                    <tr className="text-slate-500 text-left">
                      <th className="pr-3">Client</th>
                      <th className="pr-3">State</th>
                      <th className="pr-3">Invoice</th>
                      <th className="pr-3">Bill</th>
                      <th className="pr-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(lastResult.loads || []).map((l) => (
                      <tr key={l.load_id} className="text-slate-700 border-t border-slate-200">
                        <td className="pr-3 py-1">{l.client}</td>
                        <td className="pr-3 py-1">{l.state}</td>
                        <td className="pr-3 py-1">{l.invoice_number}</td>
                        <td className="pr-3 py-1">{l.bill_status}</td>
                        <td className="pr-3 py-1 text-right">₹{Number(l.amount).toLocaleString('en-IN')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="reseed-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Wipe & Reseed Test Loads?</DialogTitle>
            <DialogDescription>
              This will permanently delete all silica loads and invoice
              counters, then reseed 8 test loads. Type
              <strong> RESEED TEST LOADS </strong>
              below to confirm.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type RESEED TEST LOADS"
            data-testid="reseed-confirm-input"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="ghost"
              onClick={() => {
                setShowDialog(false);
                setConfirmText('');
              }}
              disabled={seeding}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleSeed}
              disabled={seeding || confirmText !== 'RESEED TEST LOADS'}
              data-testid="reseed-confirm-btn"
            >
              {seeding ? 'Reseeding…' : 'Confirm Reseed'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
